import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Volume2, VolumeX, Check, Download, BellRing, Banknote, RefreshCw, Home } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUEST_TYPE_INFO = {
  supervisor: { label: "طلب المشرف", icon: Bell, color: "bg-blue-600" },
  exchange: { label: "طلب صرافة", icon: Banknote, color: "bg-emerald-600" },
  change: { label: "طلب تبديل", icon: RefreshCw, color: "bg-yellow-400" },
};

const SupervisorScreen = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isRinging, setIsRinging] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const ringIntervalRef = useRef(null);
  const vibrateIntervalRef = useRef(null);
  const lastCallIdsRef = useRef(new Set());

  // Register Service Worker and Push Notifications
  useEffect(() => {
    registerServiceWorker();
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('SW registered:', registration);

      // Check if already subscribed
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        setPushEnabled(true);
        console.log('Already subscribed to push');
      }
    } catch (error) {
      console.error('SW registration failed:', error);
    }
  };

  const subscribeToPush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('يجب السماح بالإشعارات');
        return;
      }

      // Get VAPID public key from server
      const keyResponse = await fetch(`${API}/vapid-public-key`);
      const { publicKey } = await keyResponse.json();

      const registration = await navigator.serviceWorker.ready;
      
      // Convert VAPID key to Uint8Array
      const vapidKey = urlBase64ToUint8Array(publicKey);
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });

      // Send subscription to server
      await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });

      setPushEnabled(true);
      toast.success('تم تفعيل الإشعارات!');
    } catch (error) {
      console.error('Push subscription failed:', error);
      toast.error('فشل في تفعيل الإشعارات');
    }
  };

  // Helper functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  // Install PWA
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('تم تثبيت التطبيق!');
      setCanInstall(false);
    }
    setDeferredPrompt(null);
  };

  // Bell sound
  const playBellSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const frequencies = [830, 1046, 1318];
      frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        const now = audioContext.currentTime;
        gainNode.gain.setValueAtTime(0.5, now + index * 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5 + index * 0.1);
        oscillator.start(now + index * 0.1);
        oscillator.stop(now + 0.6 + index * 0.1);
      });
    } catch (e) {
      console.error("Audio error:", e);
    }
  }, [soundEnabled]);

  // Vibration
  const startVibration = useCallback(() => {
    if ("vibrate" in navigator) {
      navigator.vibrate([500, 200, 500, 200, 500]);
      vibrateIntervalRef.current = setInterval(() => {
        navigator.vibrate([500, 200, 500, 200, 500]);
      }, 2000);
    }
  }, []);

  const stopVibration = useCallback(() => {
    if ("vibrate" in navigator) navigator.vibrate(0);
    if (vibrateIntervalRef.current) {
      clearInterval(vibrateIntervalRef.current);
      vibrateIntervalRef.current = null;
    }
  }, []);

  // Ringing
  const startRinging = useCallback(() => {
    if (isRinging) return;
    setIsRinging(true);
    startVibration();
    playBellSound();
    ringIntervalRef.current = setInterval(playBellSound, 1500);
  }, [isRinging, playBellSound, startVibration]);

  const stopRinging = useCallback(() => {
    setIsRinging(false);
    stopVibration();
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
  }, [stopVibration]);

  // Fetch calls
  const fetchCalls = useCallback(async () => {
    try {
      const response = await fetch(`${API}/calls?status=pending&limit=50`);
      const data = await response.json();
      
      let hasNewCall = false;
      data.forEach((call) => {
        if (!lastCallIdsRef.current.has(call.id)) {
          hasNewCall = true;
          toast.warning(`نداء من كاش ${call.cash_number}`, { duration: 5000 });
        }
      });
      
      if (data.length > 0 && (hasNewCall || !isRinging)) {
        startRinging();
      }
      
      if (data.length === 0 && isRinging) {
        stopRinging();
      }
      
      lastCallIdsRef.current = new Set(data.map((c) => c.id));
      setCalls(data);
    } catch (error) {
      console.error("Error fetching calls:", error);
    }
  }, [startRinging, stopRinging, isRinging]);

  useEffect(() => {
    fetchCalls();
    const interval = setInterval(fetchCalls, 2000);
    return () => {
      clearInterval(interval);
      stopRinging();
    };
  }, [fetchCalls, stopRinging]);

  // Dismiss call
  const dismissCall = async (callId) => {
    try {
      await fetch(`${API}/calls/${callId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "completed" }),
      });
      const newCalls = calls.filter((c) => c.id !== callId);
      setCalls(newCalls);
      if (newCalls.length === 0) stopRinging();
      toast.success("تم");
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="flex items-center justify-between p-4 bg-slate-800">
        <button
          onClick={() => navigate("/")}
          className="w-10 h-10 bg-slate-700 hover:bg-slate-600 rounded-xl flex items-center justify-center transition-colors"
        >
          <Home className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-lg font-bold text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-emerald-400" />
          شاشة المشرف
        </h1>
        <div className="flex items-center gap-2">
          {canInstall && (
            <button onClick={handleInstall} className="bg-blue-600 text-white px-3 py-2 rounded-lg font-bold flex items-center gap-1 text-sm">
              <Download className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2 rounded-lg ${soundEnabled ? "bg-emerald-600" : "bg-slate-700"}`}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5 text-white" /> : <VolumeX className="w-5 h-5 text-slate-400" />}
          </button>
        </div>
      </header>

      {/* Success Banner */}
      {pushEnabled && (
        <div className="bg-emerald-600 p-3 text-white text-center text-sm">
          ✓ الإشعارات مفعّلة
        </div>
      )}

      {/* Calls List */}
      {calls.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[60vh] text-slate-500">
          <Bell className="w-20 h-20 mb-4 opacity-20" />
          <p className="text-xl">لا توجد طلبات</p>
        </div>
      ) : (
        <div className="p-4 space-y-4 pb-20">
          {calls.map((call) => {
            const typeInfo = REQUEST_TYPE_INFO[call.request_type] || REQUEST_TYPE_INFO.supervisor;
            const TypeIcon = typeInfo.icon;
            
            return (
              <div
                key={call.id}
                className={`${typeInfo.color} rounded-3xl p-6 ${isRinging ? "animate-pulse" : ""}`}
              >
                {/* Request Type Label */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <TypeIcon className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg">{typeInfo.label}</span>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-white/70 text-lg mb-2">كاش رقم</p>
                  <div className="text-[100px] font-black text-white leading-none">
                    {call.cash_number}
                  </div>
                </div>
                <div className="text-center text-white/80 text-lg mb-6">
                  {new Date(call.created_at).toLocaleTimeString("ar-SA")}
                </div>
                <button
                  onClick={() => dismissCall(call.id)}
                  className="w-full bg-white text-slate-800 py-4 rounded-2xl font-bold text-xl flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                  <Check className="w-6 h-6" />
                  تم
                </button>
              </div>
            );
          })}
        </div>
      )}

      {calls.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-700 text-white text-center py-4 font-bold text-lg">
          {calls.length} طلب في الانتظار
        </div>
      )}
    </div>
  );
};

export default SupervisorScreen;
